import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { explainCase } from "../../lib/api";
import { fetchCaseAudio, fetchAudioStatus, fetchFiles } from "../../lib/files";
import { Flag } from "../../lib/countries";
import { Loader2, ExternalLink, Film, Headphones, Folder, Share2, Check } from "lucide-react";

const PERSPECTIVES = [
  { key: "analyst", label: "Intelligence Brief", voice: "Onyx" },
  { key: "layperson", label: "Plain English", voice: "Nova" },
  { key: "skeptic", label: "Skeptical Review", voice: "Alloy" },
];

function Row({ label, value, mono = true }) {
  return (
    <div className="flex gap-4 py-2 border-b border-zinc-900 last:border-0">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 w-32 shrink-0 pt-0.5">
        {label}
      </span>
      <span
        className={`text-zinc-200 ${
          mono ? "font-mono text-xs" : "text-sm"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function CaseDetailDialog({ caseData, open, onClose, onOpenArchive }) {
  const [perspective, setPerspective] = useState("analyst");
  const [explanation, setExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [relatedFileCount, setRelatedFileCount] = useState(0);
  const [shareCopied, setShareCopied] = useState(false);

  const handleShare = async () => {
    if (!caseData) return;
    const url = `${window.location.origin}/api/share/case/${caseData.case_id}`;
    const data = {
      title: `PURSUE · ${caseData.case_id}`,
      text: `${caseData.location} — ${caseData.agency} ${caseData.incident_date}`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2200);
      }
    } catch (e) {
      /* user cancelled or denied */
    }
  };

  useEffect(() => {
    if (!caseData || !open) return;
    let cancelled = false;
    setLoading(true);
    setExplanation("");
    setAudioUrl(null);
    explainCase(caseData.case_id, perspective)
      .then((res) => !cancelled && setExplanation(res.explanation))
      .catch(() => !cancelled && setExplanation("Unable to generate explanation. Try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [caseData, perspective, open]);

  // Count related archive files when a case is opened
  useEffect(() => {
    if (!caseData || !open) return;
    fetchFiles({ case_id: caseData.case_id })
      .then((files) => setRelatedFileCount(files.length))
      .catch(() => setRelatedFileCount(0));
  }, [caseData, open]);

  const handlePlayAudio = async () => {
    if (audioUrl || audioLoading) return;
    setAudioLoading(true);
    try {
      const res = await fetchCaseAudio(caseData.case_id, perspective);
      if (res.status === "ready") {
        setAudioUrl(`${process.env.REACT_APP_BACKEND_URL}${res.audio_url}`);
        setAudioLoading(false);
        return;
      }
      // queued or generating — poll status
      const poll = async () => {
        for (let i = 0; i < 60; i++) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const s = await fetchAudioStatus(caseData.case_id, perspective);
            if (s.status === "ready" && s.audio_url) {
              setAudioUrl(`${process.env.REACT_APP_BACKEND_URL}${s.audio_url}`);
              return;
            }
            if (s.status === "error") {
              return;
            }
          } catch (e) {
            /* keep polling */
          }
        }
      };
      await poll();
    } catch (e) {
      /* ignore */
    } finally {
      setAudioLoading(false);
    }
  };

  if (!caseData) return null;
  const unresolved = /unresolved/i.test(caseData.classification);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="case-detail-dialog"
        className="max-w-5xl bg-zinc-950 border-zinc-800 rounded-sm p-0 overflow-hidden"
      >
        <DialogTitle className="sr-only">{caseData.case_id} details</DialogTitle>
        <DialogDescription className="sr-only">
          Declassified UAP case detail viewer for {caseData.case_id} ({caseData.location}).
        </DialogDescription>

        {/* Header */}
        <div className="border-b border-zinc-800 px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs uppercase tracking-widest text-amber-400">
                {caseData.case_id}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                  unresolved
                    ? "border-amber-500/50 text-amber-400 bg-amber-500/5"
                    : "border-emerald-500/50 text-emerald-400 bg-emerald-500/5"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    unresolved ? "bg-amber-400 animate-pulse-amber" : "bg-emerald-400"
                  }`}
                />
                {caseData.classification}
              </span>
            </div>
            <h2 className="mt-2 font-chivo font-bold text-2xl text-zinc-50 tracking-tight flex items-center gap-3">
              <Flag country={caseData.country} size={40} className="!w-8 !h-6" />
              {caseData.location}
            </h2>
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
              {caseData.agency} · {caseData.incident_date} · {caseData.type}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="case-share-btn"
              onClick={handleShare}
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 flex items-center gap-1.5"
              title="Share with OG preview"
            >
              {shareCopied ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400" />
                  <span className="text-emerald-400">Copied</span>
                </>
              ) : (
                <>
                  <Share2 className="h-3 w-3" /> Share
                </>
              )}
            </button>
            <a
              href={caseData.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 flex items-center gap-1.5"
              data-testid="case-source-link"
            >
              <ExternalLink className="h-3 w-3" /> SOURCE
            </a>
          </div>
        </div>

        {relatedFileCount > 0 && (
          <button
            data-testid="case-related-files-btn"
            onClick={() => onOpenArchive?.(caseData.case_id)}
            className="border-b border-zinc-800 px-6 py-2.5 w-full text-left flex items-center gap-2 hover:bg-zinc-900/60 transition-colors"
          >
            <Folder className="h-4 w-4 text-amber-400" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-300">
              <span className="text-amber-400">{relatedFileCount}</span> related
              file{relatedFileCount === 1 ? "" : "s"} in archive · Click to open
            </span>
          </button>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 max-h-[70vh] overflow-auto">
          {/* Left: Evidence */}
          <div className="p-6 border-r border-zinc-800">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
              ◆ Evidence Captures · {caseData.evidence_urls?.length || 0}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(caseData.evidence_urls || []).map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`evidence-thumb-${i}`}
                  className="block aspect-square border border-zinc-800 bg-zinc-900 hover:border-amber-400/60 relative overflow-hidden group"
                >
                  <img
                    src={url}
                    alt={`evidence ${i + 1}`}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/95 to-transparent px-2 py-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-amber-400">
                      [EVD-{String(i + 1).padStart(2, "0")}]
                    </span>
                  </div>
                </a>
              ))}
            </div>

            {(caseData.video_urls || []).length > 0 && (
              <>
                <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1.5">
                  <Film className="h-3 w-3" />
                  ◆ Declassified Footage · {caseData.video_urls.length}
                </div>
                <div className="space-y-3">
                  {caseData.video_urls.map((vurl, i) => {
                    const isMp4 = /\.mp4(\?|$)/i.test(vurl);
                    return (
                      <div
                        key={i}
                        data-testid={`video-block-${i}`}
                        className="border border-zinc-800 bg-zinc-900/40 p-2"
                      >
                        {isMp4 ? (
                          <video
                            controls
                            preload="metadata"
                            className="w-full aspect-video bg-black"
                            data-testid={`video-player-${i}`}
                          >
                            <source src={vurl} type="video/mp4" />
                            Your browser does not support video playback.
                          </video>
                        ) : (
                          <div className="aspect-video bg-zinc-950 flex flex-col items-center justify-center text-center px-4">
                            <Film className="h-8 w-8 text-zinc-700 mb-2" />
                            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                              {vurl.split(".").pop().toUpperCase()} · external playback
                            </span>
                            <a
                              href={vurl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2"
                            >
                              Open official source ↗
                            </a>
                          </div>
                        )}
                        <a
                          href={vurl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 break-all"
                        >
                          {vurl}
                        </a>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              ◆ Metadata
            </div>
            <div>
              <Row label="Coordinates" value={`[${caseData.lat.toFixed(4)}, ${caseData.lng.toFixed(4)}]`} />
              <Row label="Sensor" value={caseData.sensor} />
              <Row label="Duration" value={`${caseData.duration_minutes} min`} />
              <Row label="Released" value={caseData.release_date} />
              <Row label="Country" value={caseData.country} />
            </div>
          </div>

          {/* Right: AI explanation */}
          <div className="p-6 bg-zinc-950">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
              ◆ Raw Description
            </div>
            <p className="text-sm leading-relaxed text-zinc-300 mb-6">
              {caseData.description}
            </p>

            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
                ◆ AI Analyst — Claude Sonnet 4.5
              </span>
              <button
                data-testid="case-audio-btn"
                onClick={handlePlayAudio}
                disabled={audioLoading || loading}
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-amber-400 disabled:opacity-40 border border-zinc-800 hover:border-amber-400/60 px-2 py-1"
                title={`Generate spoken briefing (${PERSPECTIVES.find(p => p.key === perspective)?.voice || 'Onyx'} voice)`}
              >
                {audioLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-amber-400" />
                ) : (
                  <Headphones className="h-3 w-3" />
                )}
                {audioUrl ? "Ready" : `Listen · ${PERSPECTIVES.find(p => p.key === perspective)?.voice || 'Onyx'}`}
              </button>
            </div>
            {audioUrl && (
              <audio
                data-testid="case-audio-player"
                controls
                autoPlay
                src={audioUrl}
                className="w-full mb-3 h-8"
              />
            )}
            <div className="flex gap-2 mb-4 flex-wrap">
              {PERSPECTIVES.map((p) => (
                <button
                  key={p.key}
                  data-testid={`perspective-${p.key}`}
                  onClick={() => setPerspective(p.key)}
                  className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border transition-colors ${
                    perspective === p.key
                      ? "bg-amber-500 text-zinc-950 border-amber-500"
                      : "border-zinc-800 text-zinc-400 hover:border-amber-400/60 hover:text-amber-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="border border-zinc-800 bg-zinc-900/30 p-4 min-h-[200px]">
              {loading ? (
                <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />
                  Analyzing case data…
                </div>
              ) : (
                <p
                  data-testid="ai-explanation"
                  className="text-sm leading-relaxed text-zinc-200 whitespace-pre-wrap font-sans"
                >
                  {explanation}
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
